import React, { useState, useEffect, useRef } from 'react';
import Layout from '../components/common/Layout';
import ListPagination from '../components/common/ListPagination';
import { orderService, OrderFilters } from '../services/orderService';
import { exportService } from '../services/exportService';
import { Order } from '../types';
import { usePermissions } from '../hooks/usePermissions';
import { useAuth } from '../context/AuthContext';
import { useAppData } from '../context/AppDataContext';
import { FiCalendar, FiDollarSign, FiPackage, FiTruck, FiFileText, FiCreditCard, FiDownload, FiEdit2, FiTrash2, FiCheck, FiXCircle } from 'react-icons/fi';
import Icon from '../components/common/Icon';
import { formatDate } from '../utils/dateUtils';
import { useOrderFormModal } from '../hooks/useOrderFormModal';
import OrderFormModal from '../components/orders/OrderFormModal';

const Orders: React.FC = () => {
  const { hasPermission } = usePermissions();
  const { user } = useAuth();
  const { users, brands, suppliers, customers, refreshUsers, refreshBrands, refreshSuppliers, refreshCustomers } = useAppData();
  const [orders, setOrders] = useState<Order[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [filters, setFilters] = useState<OrderFilters>({});
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedOrderIds, setSelectedOrderIds] = useState<number[]>([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [totalCount, setTotalCount] = useState(0);

  const hasLoadedRef = useRef(false);
  const filtersInitialized = useRef(false);
  const canDeleteOrders = user?.role === 'org_admin';

  // Refresh context data on page visit and load orders list
  useEffect(() => {
    // Prevent duplicate calls from React StrictMode
    if (hasLoadedRef.current) {
      return;
    }
    hasLoadedRef.current = true;
    
    const loadData = async () => {
      // Refresh users, brands, and customers in context to get latest from other users
      await Promise.all([refreshUsers(), refreshBrands(), refreshSuppliers(), refreshCustomers()]);
      // Always fetch fresh orders list
      await loadOrders();
      // Set after load completes to prevent filters useEffect from running on initial mount
      filtersInitialized.current = true;
    };
    loadData();
  }, []);

  // Reload orders when filters or page change (but not on initial mount)
  useEffect(() => {
    if (filtersInitialized.current) {
      loadOrders();
    }
  }, [filters, page, pageSize]);

  const loadOrders = async () => {
    if (loading) {
      return;
    }
    try {
      setLoading(true);
      const response = await orderService.getAll({
        ...filters,
        limit: pageSize,
        offset: (page - 1) * pageSize,
      });
      const total = response.total ?? 0;
      const maxPage = Math.max(1, Math.ceil(total / pageSize));
      if (page > maxPage) {
        setPage(maxPage);
        return;
      }
      setOrders(response.data);
      setTotalCount(total);
      setError('');
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const orderModal = useOrderFormModal({ onSaved: loadOrders });

  const handleDelete = async (id: number) => {
    if (!canDeleteOrders) {
      setError('Only org-admin can delete orders');
      return;
    }
    if (!window.confirm('Are you sure you want to delete this order?')) {
      return;
    }
    try {
      await orderService.delete(id);
      // Refresh orders list
      loadOrders();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to delete order');
    }
  };

  const handleSearch = () => {
    setSelectedOrderIds([]);
    setPage(1);
    setFilters({ ...filters, search: searchTerm });
  };

  const toggleOrderSelection = (orderId: number) => {
    setSelectedOrderIds((prev) =>
      prev.includes(orderId) ? prev.filter((id) => id !== orderId) : [...prev, orderId]
    );
  };

  const toggleSelectAllCurrentPage = () => {
    const currentOrderIds = orders.map((o) => o.id);
    const allSelected = currentOrderIds.length > 0 && currentOrderIds.every((id) => selectedOrderIds.includes(id));
    if (allSelected) {
      setSelectedOrderIds((prev) => prev.filter((id) => !currentOrderIds.includes(id)));
    } else {
      setSelectedOrderIds((prev) => Array.from(new Set([...prev, ...currentOrderIds])));
    }
  };

  const handleBulkDelete = async () => {
    if (!canDeleteOrders) {
      setError('Only org-admin can bulk delete orders');
      return;
    }
    if (selectedOrderIds.length === 0) {
      return;
    }
    if (!window.confirm(
      `Delete ${selectedOrderIds.length} selected order(s)?\n\nThis action cannot be undone.`
    )) {
      return;
    }

    try {
      setLoading(true);
      const result = await orderService.bulkDelete(selectedOrderIds);
      const errorSummary = result.errors.length > 0 ? ` (${result.errors.length} issue(s))` : '';
      setError(`Bulk delete complete: ${result.deleted_count}/${result.total_selected} deleted${errorSummary}`);
      setSelectedOrderIds([]);
      await loadOrders();
    } catch (err: any) {
      setError(err.response?.data?.message || 'Bulk delete failed');
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value?: number) => {
    if (!value) return '-';
    return `₹${value.toLocaleString('en-IN')}`;
  };


  const getStatusIcon = (status: string | undefined, label: string) => {
    const normalized = (status || 'not_set').toLowerCase();
    const isCompleted = normalized === 'completed';
    const title = `${label}: ${status || 'not set'}`;
    return (
      <span
        title={title}
        className={`inline-flex items-center justify-center w-5 h-5 rounded-full ${isCompleted ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}
      >
        <Icon component={isCompleted ? FiCheck : FiXCircle} className="w-3 h-3" />
      </span>
    );
  };

  return (
    <Layout>
      <div className="space-y-3 sm:space-y-4">
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900">Order Management</h1>
          <div className="flex gap-2 w-full sm:w-auto">
            {canDeleteOrders && (
              <button
                type="button"
                onClick={handleBulkDelete}
                disabled={selectedOrderIds.length === 0 || loading}
                className="bg-red-600 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-red-700 text-sm sm:text-base font-medium w-full sm:w-auto whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Delete Selected ({selectedOrderIds.length})
              </button>
            )}
            {user?.role === 'org_admin' && (
              <button
                onClick={async () => {
                  try {
                    await exportService.export('orders', filters);
                  } catch (err: any) {
                    setError(err.response?.data?.error || 'Failed to export orders');
                  }
                }}
                className="bg-green-600 text-white px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-green-700 text-sm sm:text-base font-medium flex items-center gap-2 w-full sm:w-auto justify-center"
              >
                <Icon component={FiDownload} className="w-4 h-4" />
                <span>Export</span>
              </button>
            )}
            <button
              onClick={() => orderModal.openForCreate()}
              className="bg-yellow-500 text-black-950 px-3 py-1.5 sm:px-4 sm:py-2 rounded-md hover:bg-yellow-600 text-sm sm:text-base font-medium w-full sm:w-auto font-bold"
            >
              Add Order
            </button>
          </div>
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 text-red-800 px-4 py-3 rounded text-sm">
            {error}
          </div>
        )}

        {/* Filters */}
        <div className="bg-white p-3 sm:p-4 rounded-lg shadow-sm">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-6 gap-2 sm:gap-3 items-end">
            <div>
              <input
                type="date"
                value={filters.date_from || ''}
                onChange={(e) => setFilters({ ...filters, date_from: e.target.value || undefined })}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              />
            </div>
            <div>
              <input
                type="date"
                value={filters.date_to || ''}
                onChange={(e) => setFilters({ ...filters, date_to: e.target.value || undefined })}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              />
            </div>
            <div>
              <select
                value={filters.sales_officer_id || ''}
                onChange={(e) => setFilters({ ...filters, sales_officer_id: e.target.value ? parseInt(e.target.value) : undefined })}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="">All Officers</option>
                {users.map((user) => (
                  <option key={user.id} value={user.id}>{user.name}</option>
                ))}
              </select>
            </div>
            <div>
              <select
                value={filters.brand_id || ''}
                onChange={(e) => setFilters({ ...filters, brand_id: e.target.value ? parseInt(e.target.value) : undefined })}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              >
                <option value="">All Brands</option>
                {brands.map((brand) => (
                  <option key={brand.id} value={brand.id}>{brand.name}</option>
                ))}
              </select>
            </div>
            <div>
              <input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by order number, customer code..."
                onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                className="w-full border border-gray-300 rounded-md px-2 py-1.5 sm:px-3 sm:py-2 text-sm"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="button"
                onClick={handleSearch}
                className="bg-yellow-500 text-black-950 px-3 py-1.5 rounded-md hover:bg-yellow-600 text-sm font-medium flex-1 sm:flex-initial font-bold whitespace-nowrap"
              >
                Search
              </button>
              <button
                type="button"
                onClick={() => {
                  setFilters({});
                  setSearchTerm('');
                  setSelectedOrderIds([]);
                  setPage(1);
                }}
                className="bg-gray-200 text-gray-700 px-3 py-1.5 rounded-md hover:bg-gray-300 text-sm font-medium flex-1 sm:flex-initial whitespace-nowrap"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        {loading ? (
          <div className="text-center py-6 text-sm text-gray-600">Loading...</div>
        ) : (
          <div className="bg-white shadow-sm overflow-hidden rounded-lg">
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    {canDeleteOrders && (
                      <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">
                        <input
                          type="checkbox"
                          checked={orders.length > 0 && orders.every((o) => selectedOrderIds.includes(o.id))}
                          onChange={toggleSelectAllCurrentPage}
                          aria-label="Select all orders on page"
                        />
                      </th>
                    )}
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Order No</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Order Date</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Customer Name</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Brand</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Company Sales Officer</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Sales Officer</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Brand/Supplier</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Prod Type</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Quantity</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Price</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Project Location</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider">Status</th>
                    <th className="px-2 sm:px-3 py-1.5 text-left text-[11px] font-bold text-gray-500 uppercase tracking-wider min-w-[96px]">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {orders.map((order) => (
                    <tr key={order.id} className="hover:bg-gray-50">
                      {canDeleteOrders && (
                        <td className="px-2 py-1.5">
                          <input
                            type="checkbox"
                            checked={selectedOrderIds.includes(order.id)}
                            onChange={() => toggleOrderSelection(order.id)}
                            aria-label={`Select order ${order.order_number || order.id}`}
                          />
                        </td>
                      )}
                      <td className="px-2 sm:px-3 py-1.5 text-xs font-medium text-gray-900">{order.order_number || '-'}</td>
                      <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-900">{formatDate(order.order_date)}</td>
                      <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500">{order.customer_name || order.customer_code || '-'}</td>
                      <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500">{order.brand_name || '-'}</td>
                      <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500">{order.company_sales_officer ?? '-'}</td>
                      <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500">{order.sales_officer_name || '-'}</td>
                      <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500">{order.supplier_name ? `${order.brand_name || ''}/${order.supplier_name}`.replace(/^\//, '') : (order.brand_name || '-')}</td>
                      <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500">{order.product_type || '-'}</td>
                      <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500">{order.quantity ?? '-'}</td>
                      <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500">{order.price_per_bag != null ? formatCurrency(order.price_per_bag) : '-'}</td>
                      <td className="px-2 sm:px-3 py-1.5 text-xs text-gray-500">{[order.district, order.destination].filter(Boolean).join(', ') || '-'}</td>
                      <td className="px-2 sm:px-3 py-1.5">
                        <div className="flex items-center gap-1">
                          {getStatusIcon(order.payment_status, 'Payment')}
                          {getStatusIcon(order.po_status, 'PO')}
                          {getStatusIcon(order.dispatch_status, 'Dispatch')}
                          {getStatusIcon(order.invoice_status, 'Invoice')}
                        </div>
                      </td>
                      <td className="px-2 sm:px-3 py-1.5 whitespace-nowrap text-xs font-medium">
                        <button
                          onClick={() => orderModal.openForEdit(order)}
                          className="inline-flex items-center justify-center p-1.5 rounded hover:bg-indigo-50 text-indigo-600 hover:text-indigo-900"
                          title="Edit order"
                          aria-label="Edit order"
                        >
                          <Icon component={FiEdit2} className="w-3.5 h-3.5" />
                        </button>
                        {canDeleteOrders && (
                          <button
                            onClick={() => handleDelete(order.id)}
                            className="inline-flex items-center justify-center p-1.5 rounded hover:bg-red-50 text-red-600 hover:text-red-900 ml-1"
                            title="Delete order"
                            aria-label="Delete order"
                          >
                            <Icon component={FiTrash2} className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {orders.length === 0 && !loading && (
              <div className="text-center py-8 text-sm text-gray-500">No orders found</div>
            )}
            <ListPagination
              page={page}
              perPage={pageSize}
              total={totalCount}
              onPageChange={setPage}
              onPerPageChange={setPageSize}
            />
          </div>
        )}

        <OrderFormModal {...orderModal} />
      </div>
    </Layout>
  );
};

export default Orders;

